import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { PlatformAdminLink } from '@/pages/admin/PlatformAdminLink';
import { LogOut, Moon, Sun } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BrandLogo } from '@/components/brand/BrandMark';
import { useFirebase } from '@mesterplan/shared-firebase';
import { signOut } from 'firebase/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useTheme } from '@/contexts/ThemeContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useAuth } from '@mesterplan/auth-react';
import { WhatsNewSidebarRow } from '@/components/whats-new/WhatsNewSidebarRow';
import { TrackrsAttentionNavBadge } from '@/components/integration/attention/TrackrsAttentionNavBadge';
import { calculationEnabledFor } from '@/contexts/companyMappers';
import { getNavigationSections, type NavigationItem, type NavigationSection } from './navigation';

import { idpEnabled } from '@/lib/idp';

const navItemClass =
  'flex w-full items-center gap-3 px-3 py-[10px] rounded-lg text-[14px] font-medium transition-all duration-150 text-muted dark:text-dark-muted hover:bg-primary-50 dark:hover:bg-dark-hover hover:text-primary dark:hover:text-primary-400';

function renderNavItem(
  item: NavigationItem,
  onAction?: (action: NonNullable<NavigationItem['action']>) => void,
): ReactNode {
  const action = item.action;
  if (action) {
    return (
      <button
        key={action}
        type="button"
        onClick={() => onAction?.(action)}
        className={cn(navItemClass, 'text-left text-primary dark:text-primary-400')}
      >
        <item.icon className="h-[20px] w-[20px] flex-shrink-0 stroke-[1.5]" />
        <span className="truncate">{item.label}</span>
      </button>
    );
  }
  if (item.externalHref) {
    return (
      <a
        key={item.path}
        href={item.externalHref}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-3 py-[10px] rounded-lg text-[14px] font-medium transition-all duration-150 text-muted dark:text-dark-muted hover:bg-primary-50 dark:hover:bg-dark-hover hover:text-primary dark:hover:text-primary-400"
      >
        <item.icon className="h-[20px] w-[20px] flex-shrink-0 stroke-[1.5]" />
        <span>{item.label}</span>
      </a>
    );
  }
  return (
    <NavLink
      key={item.path}
      to={item.path}
      end={item.path === '/'}
      className={({ isActive }: { isActive: boolean }) =>
        cn(
          'flex items-center gap-3 px-3 py-[10px] rounded-lg text-[14px] font-medium transition-all duration-150',
          isActive
            ? 'bg-primary text-white shadow-sm'
            : 'text-muted dark:text-dark-muted hover:bg-primary-50 dark:hover:bg-dark-hover hover:text-primary dark:hover:text-primary-400'
        )
      }
    >
      {({ isActive }: { isActive: boolean }) => (
        <>
          <item.icon className={cn(
            'h-[20px] w-[20px] flex-shrink-0 stroke-[1.5]',
            isActive ? 'text-white' : ''
          )} />
          <span>{item.label}</span>
          {item.badge}
        </>
      )}
    </NavLink>
  );
}

function NavSection({
  title,
  items,
  onAction,
}: {
  title: string;
  items: NavigationItem[];
  onAction?: (action: NonNullable<NavigationItem['action']>) => void;
}) {
  return (
    <div className="mb-1">
      <p className="px-6 py-3 text-[11px] font-bold text-muted dark:text-dark-muted uppercase tracking-widest">
        {title}
      </p>
      <nav className="px-4 space-y-0.5">
        {items.map((item) => renderNavItem(item, onAction))}
      </nav>
    </div>
  );
}

// User section for IDP mode — uses useAuth() which requires OidcAuthProvider.
// Must be a separate component to avoid calling useAuth() in Firebase mode.
function IdpUserSection() {
  const { user, logout } = useAuth();
  const displayName = user?.displayName || user?.email || 'Bruger';
  const initial = displayName.charAt(0).toUpperCase();
  return (
    <div className="p-4 border-t border-bordergray dark:border-dark-border">
      <div className="flex items-center gap-3 p-3 rounded-lg bg-surface dark:bg-dark-hover">
        <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center ring-2 ring-white dark:ring-dark-border">
          <span className="text-primary dark:text-primary-400 font-semibold text-sm">{initial}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-dark dark:text-dark-text truncate">{displayName}</p>
          <p className="text-[12px] text-muted dark:text-dark-muted truncate">{user?.email}</p>
        </div>
        <button
          onClick={() => void logout()}
          className="p-2 rounded-lg text-muted dark:text-dark-muted hover:text-error hover:bg-error-light dark:hover:bg-error/20 transition-colors"
          title="Log ud"
        >
          <LogOut className="h-[18px] w-[18px]" />
        </button>
      </div>
    </div>
  );
}

interface SidebarProps {
  /** Replaces the permission-filtered company menu (the supplier shell brings its own). */
  sections?: NavigationSection[];
  /** Rendered between the logo and the menu (the supplier shell's org chip). */
  header?: ReactNode;
}

export default function Sidebar({ sections, header }: SidebarProps = {}) {
  const { auth, currentUser } = useFirebase();
  // This mode-aware hook uses .NET permissions under IDP and Firebase otherwise.
  const { hasPermission } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const { selectedCompany, isPartnerUser, isPartnerCompany, isSupplierSession, supplierOrg, leaveSupplierSession } = useCompany();

  // In a supplier session the full company record is not readable, so the module switch is
  // unknown: show AI-analyse and let the permission filter decide.
  const calculationEnabled = calculationEnabledFor(selectedCompany, isSupplierSession);
  const navigationSections = sections ?? getNavigationSections({
    isPartnerUser,
    isPartnerCompany,
    calculationEnabled,
    isSupplierSession,
    supplierName: supplierOrg?.name,
    hasPermission,
  });
  const sectionsWithAttentionBadge = idpEnabled && !isPartnerUser && !isPartnerCompany && !sections
    ? navigationSections.map((section) => ({
        ...section,
        items: section.items.map((item) =>
          item.path === '/projects'
            ? { ...item, badge: <TrackrsAttentionNavBadge /> }
            : item,
        ),
      }))
    : navigationSections;

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-[270px] bg-white dark:bg-dark-card border-r border-bordergray dark:border-dark-border flex flex-col">
      {/* Logo */}
      <div className="h-[70px] flex items-center justify-between px-6">
        <BrandLogo textClassName="text-22" />
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg text-muted dark:text-dark-muted hover:bg-surface dark:hover:bg-dark-hover hover:text-dark dark:hover:text-dark-text transition-colors"
          title={theme === 'dark' ? 'Skift til lys tilstand' : 'Skift til mørk tilstand'}
        >
          {theme === 'dark' ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </button>
      </div>

      <PlatformAdminLink />
      {header}
      {/* Navigation */}
      <div className="flex-1 overflow-y-auto sidebar-scroll py-2">
        {sectionsWithAttentionBadge.map((section) => (
          <NavSection
            key={section.title}
            title={section.title}
            items={section.items}
            onAction={(action) => { if (action === 'leaveSupplierSession') leaveSupplierSession(); }}
          />
        ))}
      </div>

      {/* Fixed so the unread count never scrolls away; present in every menu variant (§5.3). */}
      {idpEnabled && <WhatsNewSidebarRow />}

      {/* User Section */}
      {idpEnabled ? (
        <IdpUserSection />
      ) : (
        <div className="p-4 border-t border-bordergray dark:border-dark-border">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-surface dark:bg-dark-hover">
            <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center ring-2 ring-white dark:ring-dark-border">
              <span className="text-primary dark:text-primary-400 font-semibold text-sm">
                {currentUser?.displayName?.charAt(0) || currentUser?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold text-dark dark:text-dark-text truncate">
                {currentUser?.displayName || 'Bruger'}
              </p>
              <p className="text-[12px] text-muted dark:text-dark-muted truncate">
                {currentUser?.email}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-muted dark:text-dark-muted hover:text-error hover:bg-error-light dark:hover:bg-error/20 transition-colors"
              title="Log ud"
            >
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      )}
    </aside>
  );
}
